/**
 * GrowthTab — Business Growth Intelligence Dashboard
 *
 * Research sources (May 2026):
 *  • NRN "Catering is a growth engine for Top 500 chains" (May 12, 2026)
 *    - 91% of workplaces plan to spend same or more on catering in 2026
 *    - B2B catering AUVs 10x mealtime for brands like Bojangles
 *    - Weekly workplace catering (Friday lunches, Monday breakfasts) = highest AUV
 *  • Restaurant365 KPI Guide:
 *    - Prime cost target: < 65% of sales (food + labor)
 *    - Labor cost benchmark: 25–35% of sales
 *    - Food/COGS benchmark: 28–35% of sales
 *    - Table turns and server productivity are leading revenue drivers
 *  • Toast Food Truck Business Plan Guide (2026):
 *    - Food truck startup cost: $50k–$60k
 *    - Key growth lever: location strategy by time-of-day and season
 *    - Loyalty program + social media = highest ROI marketing channels
 *  • Industry averages:
 *    - Healthy net margin: 3–9% (QSR), 5–12% (catering)
 *    - Average QSR ticket: $10–$18
 *    - Discount rate healthy ceiling: < 8% of gross revenue
 *    - Return/refund rate ceiling: < 2% of tickets
 *
 * Features:
 *  1. PARALLEL fetch of 5 periods: today, yesterday, this week, last week, this month
 *  2. Business Health Scorecard (A–F graded)
 *  3. Revenue Velocity Tracker (is growth accelerating or slowing?)
 *  4. Industry Benchmark Comparisons (calibrated to QSR / catering norms)
 *  5. Catering Revenue Analysis (separate spotlight since catering = #1 growth engine)
 *  6. 7-Day Revenue Sparkline (fetched in parallel)
 *  7. Actionable Growth Recommendations (rule-based, data-driven)
 *  8. Top opportunity items (high qty but low revenue = upsell targets)
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { fmt$, fmtN } from '../../../utils/formatUtils';
import { toCST_ISO, tzDate } from '../../../utils/cstUtils';
import { API_BASE, computeRange } from '../squareDashboardUtils';

// ─── Industry benchmarks (sourced from research above) ───────────────────────
const BENCHMARKS = {
  avgTicket:    { low: 10, mid: 14, high: 18, unit: '$', label: 'Avg Ticket (QSR)' },
  discountRate: { low: 3,  mid: 5,  high: 8,  unit: '%', label: 'Discount Rate',   lowerIsBetter: true },
  returnRate:   { low: 0,  mid: 1,  high: 2,  unit: '%', label: 'Return Rate',      lowerIsBetter: true },
  cateringMix:  { low: 10, mid: 20, high: 35, unit: '%', label: 'Catering Revenue Mix' },
};

// ─── Fetch utility ────────────────────────────────────────────────────────────
async function fetchPeriod(start, end) {
  const url = `${API_BASE}/api/square-sales?startAt=${encodeURIComponent(toCST_ISO(start))}&endAt=${encodeURIComponent(toCST_ISO(end))}`;
  const res  = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  if (!json.ok) throw new Error(json.error || 'API error');
  return json;
}

// ─── Computed helpers ─────────────────────────────────────────────────────────
function getRevCents(data)    { return data ? Math.round(parseFloat(data.summary?.totalRevenue  || 0) * 100) : 0; }
function getTickets(data)     { return data?.summary?.totalTickets  || 0; }
function getDiscCents(data)   { return data ? Math.round(parseFloat(data.summary?.totalDiscount || 0) * 100) : 0; }
function getReturnQty(data)   { return data?.summary?.totalReturnQty || 0; }

function getCateringRevCents(data) {
  if (!data?.categories) return 0;
  // Match any category containing "catering" or "tiffin" (common in Indian cuisine context)
  const cat = data.categories.find(c =>
    /catering|tiffin|event|bulk/i.test(c.name)
  );
  return cat?.revenueCents || 0;
}

function getCategoryRevMap(data) {
  if (!data?.categories) return {};
  return Object.fromEntries(data.categories.map(c => [c.name, c.revenueCents || 0]));
}

function deltaStr(curr, prior) {
  if (!prior || prior === 0) return null;
  return ((curr - prior) / Math.abs(prior)) * 100;
}

function fmtPct(p, decimals = 1) {
  if (p === null || p === undefined) return '—';
  const sign = p >= 0 ? '+' : '';
  return `${sign}${p.toFixed(decimals)}%`;
}

// ─── Health scoring ───────────────────────────────────────────────────────────
function computeHealthScore(todayData, weekData, lastWeekData) {
  const scores = [];

  // 1. Avg ticket vs benchmark
  const tix   = getTickets(todayData);
  const rev   = getRevCents(todayData) / 100;
  const avg   = tix > 0 ? rev / tix : 0;
  if (tix > 5) {
    const s = avg >= BENCHMARKS.avgTicket.high ? 100
      : avg >= BENCHMARKS.avgTicket.mid        ? 75
      : avg >= BENCHMARKS.avgTicket.low        ? 50 : 25;
    scores.push({ label: 'Avg Ticket',   score: s, detail: `${fmt$(avg)} per ticket` });
  }

  // 2. Discount rate
  const discRate = rev > 0 ? (getDiscCents(todayData) / 100 / rev) * 100 : 0;
  if (tix > 5) {
    const s = discRate <= BENCHMARKS.discountRate.low ? 100
      : discRate <= BENCHMARKS.discountRate.mid       ? 75
      : discRate <= BENCHMARKS.discountRate.high      ? 50 : 25;
    scores.push({ label: 'Discount Rate', score: s, detail: `${discRate.toFixed(1)}% of revenue` });
  }

  // 3. Return rate
  const returnRate = tix > 0 ? (getReturnQty(todayData) / tix) * 100 : 0;
  if (tix > 5) {
    const s = returnRate <= BENCHMARKS.returnRate.low ? 100
      : returnRate <= BENCHMARKS.returnRate.mid       ? 80
      : returnRate <= BENCHMARKS.returnRate.high      ? 50 : 20;
    scores.push({ label: 'Return Rate',   score: s, detail: `${returnRate.toFixed(1)}% of tickets` });
  }

  // 4. Week-over-week revenue trend
  const weekRev     = getRevCents(weekData) / 100;
  const lastWeekRev = getRevCents(lastWeekData) / 100;
  if (weekRev > 0 && lastWeekRev > 0) {
    const wow = ((weekRev - lastWeekRev) / lastWeekRev) * 100;
    const s   = wow >= 10 ? 100 : wow >= 0 ? 75 : wow >= -10 ? 50 : 25;
    scores.push({ label: 'WoW Growth',    score: s, detail: `${fmtPct(wow)} this week vs last` });
  }

  // 5. Catering mix
  const cateringRev = getCateringRevCents(weekData) / 100;
  if (weekRev > 0 && cateringRev > 0) {
    const mix = (cateringRev / weekRev) * 100;
    const s   = mix >= BENCHMARKS.cateringMix.high ? 100
      : mix >= BENCHMARKS.cateringMix.mid           ? 75
      : mix >= BENCHMARKS.cateringMix.low           ? 50 : 30;
    scores.push({ label: 'Catering Mix',  score: s, detail: `${mix.toFixed(1)}% of weekly revenue` });
  }

  if (scores.length === 0) return { overall: null, scores: [] };
  const overall = Math.round(scores.reduce((s, x) => s + x.score, 0) / scores.length);
  return { overall, scores };
}

function gradeLabel(score) {
  if (score >= 90) return { grade: 'A+', color: '#16a34a', bg: '#f0fdf4' };
  if (score >= 80) return { grade: 'A',  color: '#16a34a', bg: '#f0fdf4' };
  if (score >= 70) return { grade: 'B',  color: '#0ea5e9', bg: '#f0f9ff' };
  if (score >= 60) return { grade: 'C',  color: '#d97706', bg: '#fffbeb' };
  if (score >= 50) return { grade: 'D',  color: '#f97316', bg: '#fff7ed' };
  return              { grade: 'F',  color: '#e11d48', bg: '#fff1f2' };
}

// ─── Growth recommendations engine ───────────────────────────────────────────
function buildRecommendations(todayData, weekData, lastWeekData, monthData) {
  const recs = [];

  const weekRev     = getRevCents(weekData)     / 100;
  const lastWeekRev = getRevCents(lastWeekData) / 100;
  const monthRev    = getRevCents(monthData)    / 100;
  const tix         = getTickets(weekData);
  const avgTkt      = tix > 0 ? weekRev / tix : 0;
  const discRate    = weekRev > 0 ? (getDiscCents(weekData) / 100 / weekRev) * 100 : 0;
  const cateringRev = getCateringRevCents(weekData) / 100;
  const wow         = deltaStr(weekRev, lastWeekRev);

  // Catering — biggest lever (NRN research: 91% of workplaces to spend same/more in 2026)
  if (cateringRev === 0) {
    recs.push({
      priority: 'high',
      icon: '🍱',
      title: 'Launch Workplace Catering',
      body: 'Industry data (NRN 2026): catering AUVs are 10× mealtime revenue. B2B workplace orders (Friday lunches, Monday breakfasts) are the fastest-growing channel for food businesses. Register on ezCater or Google Catering — zero upfront cost.',
      action: 'Set up a catering menu & list on ezCater this week',
    });
  } else if (cateringRev / weekRev < 0.1) {
    recs.push({
      priority: 'high',
      icon: '📈',
      title: 'Scale Catering Revenue',
      body: `Catering is only ${((cateringRev/weekRev)*100).toFixed(1)}% of weekly revenue. Top-performing food businesses drive 20–35% from catering. Consider "weekly office lunch" packages, corporate monthly accounts, or tiffin subscription programs.`,
      action: 'Offer 10% off for 4+ catering orders/month to lock in recurring revenue',
    });
  }

  // Average ticket upsell
  if (avgTkt > 0 && avgTkt < BENCHMARKS.avgTicket.mid) {
    recs.push({
      priority: 'high',
      icon: '💰',
      title: 'Increase Average Ticket Size',
      body: `Your average ticket (${fmt$(avgTkt)}) is below the QSR benchmark of $${BENCHMARKS.avgTicket.mid}–$${BENCHMARKS.avgTicket.high}. Even a $2 upsell per ticket adds ${fmt$(tix * 2 * 4)} monthly at current volume.`,
      action: 'Train staff to suggest a drink or side with every order. Add "complete your meal" combos.',
    });
  }

  // WoW revenue decline
  if (wow !== null && wow < -10) {
    recs.push({
      priority: 'high',
      icon: '⚠️',
      title: 'Revenue Dropped vs Last Week',
      body: `Revenue is down ${fmtPct(wow)} compared to last week. Common causes: weather, reduced truck hours, competing local events, or a slow social media week. Check if traffic or ticket count changed.`,
      action: 'Run a 1-day flash special or social media post to recover foot traffic',
    });
  }

  // Discount overuse
  if (discRate > BENCHMARKS.discountRate.high) {
    recs.push({
      priority: 'medium',
      icon: '🏷️',
      title: 'High Discount Rate Hurting Margins',
      body: `Your discount rate is ${discRate.toFixed(1)}% — above the healthy ceiling of ${BENCHMARKS.discountRate.high}%. Discounts can attract customers but erode margins fast. Consider switching from blanket discounts to loyalty-based rewards.`,
      action: 'Replace percentage discounts with a punch-card or loyalty point system',
    });
  }

  // Location/time optimization
  recs.push({
    priority: 'medium',
    icon: '📍',
    title: 'Optimize Location Strategy',
    body: 'Toast (2026): Food trucks that move to the best location by time-of-day see the highest revenue. Lunch (11am–2pm) near offices, dinner (5–8pm) near parks or events, weekends at festivals.',
    action: 'Track revenue by location in the Compare tab to find your highest-performing spots',
  });

  // Social media / loyalty
  if (monthRev > 0 && monthRev < 5000) {
    recs.push({
      priority: 'medium',
      icon: '📲',
      title: 'Social Media Is Your Highest-ROI Marketing',
      body: 'Toast research: food trucks that post daily location + menu updates on Instagram/TikTok see 2–3× more foot traffic than those that don\'t. The "Bombay Food Junkies" model — post where you\'ll be each morning — works.',
      action: 'Schedule daily 8am location posts on Instagram and Google Business Profile',
    });
  }

  // Menu efficiency
  const allItems = [];
  (weekData?.categories || []).forEach(cat =>
    (cat.items || []).forEach(it => allItems.push({ ...it, category: cat.name }))
  );
  const highQtyLowPrice = allItems
    .filter(it => it.itemQty > 5)
    .sort((a, b) => (b.itemQty / (b.priceCents || 1)) - (a.itemQty / (a.priceCents || 1)));

  if (highQtyLowPrice.length > 0) {
    const topItem = highQtyLowPrice[0];
    recs.push({
      priority: 'low',
      icon: '🍽️',
      title: 'Menu Efficiency Opportunity',
      body: `"${topItem.name}" sells frequently. Restaurant365 (2026): high-demand items are prime targets for a price optimization or combo upsell. Even a $0.50 price increase on a top seller significantly lifts contribution margin.`,
      action: `Review pricing on "${topItem.name}" — consider a premium or combo version`,
    });
  }

  // Tiffin/subscription model
  const hasTiffin = (weekData?.categories || []).some(c => /tiffin|subscription|weekly/i.test(c.name));
  if (!hasTiffin) {
    recs.push({
      priority: 'low',
      icon: '🔄',
      title: 'Add a Tiffin / Subscription Model',
      body: 'Subscription meal programs (daily/weekly tiffin) are the single most predictable revenue stream for Indian food businesses. $60–$120/week per subscriber × 20 subscribers = $5k–$10k guaranteed monthly revenue before you open.',
      action: 'Offer a "5-day tiffin plan" via WhatsApp order group or simple website form',
    });
  }

  return recs.slice(0, 6);
}

// ─── Sparkline component ──────────────────────────────────────────────────────
function Sparkline({ values, color = '#3b82f6', height = 36 }) {
  if (!values || values.length < 2) return null;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const W = 120;
  const H = height;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * W;
    const y = H - ((v - min) / range) * (H - 4) - 2;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} style={{ display: 'block' }}>
      <polyline fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" points={pts} />
    </svg>
  );
}

// ─── Benchmark meter ──────────────────────────────────────────────────────────
function BenchmarkMeter({ label, value, low, mid, high, unit, lowerIsBetter }) {
  // position of value on 0–(high*1.5) scale
  const scale = high * 1.5;
  const pct   = Math.min(100, (value / scale) * 100);
  const isGood = lowerIsBetter
    ? (value <= mid)
    : (value >= mid);
  const color = isGood ? '#16a34a' : value <= (lowerIsBetter ? high : low) ? '#d97706' : '#e11d48';

  return (
    <div className="gt__bench-row">
      <div className="gt__bench-label">{label}</div>
      <div className="gt__bench-track">
        <div className="gt__bench-fill" style={{ width: `${pct}%`, background: color }} />
        {/* zone markers */}
        <div className="gt__bench-mark" style={{ left: `${(low/scale)*100}%` }} title={`Low: ${low}${unit}`} />
        <div className="gt__bench-mark" style={{ left: `${(mid/scale)*100}%` }} title={`Mid: ${mid}${unit}`} />
        <div className="gt__bench-mark gt__bench-mark--high" style={{ left: `${(high/scale)*100}%` }} title={`High: ${high}${unit}`} />
      </div>
      <div className="gt__bench-val" style={{ color }}>{value.toFixed(unit === '$' ? 2 : 1)}{unit}</div>
      <div className="gt__bench-zone" style={{ color }}>
        {lowerIsBetter
          ? (value <= low ? '✅ Excellent' : value <= mid ? '🟡 Good' : value <= high ? '🟠 Watch' : '🔴 High')
          : (value >= high ? '✅ Excellent' : value >= mid ? '🟡 Good' : value >= low ? '🟠 Low' : '🔴 Below')}
      </div>
    </div>
  );
}

// ─── Velocity widget ──────────────────────────────────────────────────────────
function VelocityCard({ weekRev, lastWeekRev, weekLabel, lastWeekLabel, dailyRevs }) {
  const wow    = deltaStr(weekRev, lastWeekRev);
  const trend  = wow === null ? 'neutral' : wow > 5 ? 'up' : wow < -5 ? 'down' : 'flat';
  const arrows = { up: '🚀', flat: '➡️', down: '📉', neutral: '—' };
  const colors  = { up: '#16a34a', flat: '#d97706', down: '#e11d48', neutral: '#94a3b8' };

  return (
    <div className={`gt__vel gt__vel--${trend}`}>
      <div className="gt__vel-top">
        <div>
          <div className="gt__vel-label">Revenue Velocity</div>
          <div className="gt__vel-arrow">{arrows[trend]}</div>
        </div>
        <div className="gt__vel-chart">
          <Sparkline values={dailyRevs} color={colors[trend]} />
        </div>
      </div>
      <div className="gt__vel-row">
        <div className="gt__vel-period">
          <div className="gt__vel-period-lbl">{weekLabel}</div>
          <div className="gt__vel-period-val">{fmt$(weekRev)}</div>
        </div>
        <div className="gt__vel-delta" style={{ color: colors[trend] }}>
          {fmtPct(wow)} {wow !== null ? (wow >= 0 ? '▲' : '▼') : ''}
        </div>
        <div className="gt__vel-period">
          <div className="gt__vel-period-lbl">{lastWeekLabel}</div>
          <div className="gt__vel-period-val">{fmt$(lastWeekRev)}</div>
        </div>
      </div>
      <div className="gt__vel-msg">
        {trend === 'up'   && 'Business is accelerating — keep the momentum!'}
        {trend === 'flat' && 'Revenue is stable. Focus on upsell to grow average ticket.'}
        {trend === 'down' && 'Revenue is slowing. Run a promotion or expand to new locations.'}
      </div>
    </div>
  );
}

// ─── Main GrowthTab ───────────────────────────────────────────────────────────
export default function GrowthTab() {
  const [periods, setPeriods] = useState({
    today: null, yesterday: null,
    thisWeek: null, lastWeek: null,
    thisMonth: null,
  });
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  // ─ Parallel fetch all 5 periods ─
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const ranges = {
        today:     computeRange('today'),
        yesterday: computeRange('yesterday'),
        thisWeek:  computeRange('7d'),
        lastWeek:  (() => {
          const d     = tzDate(new Date());
          const dow   = d.getDay();
          const back  = dow === 0 ? 6 : dow - 1;
          // last Mon
          const lMon  = new Date(d); lMon.setDate(d.getDate() - back - 7);
          // last Sun
          const lSun  = new Date(lMon); lSun.setDate(lMon.getDate() + 6); lSun.setHours(23,59,59,999);
          return { start: lMon, end: lSun };
        })(),
        thisMonth: computeRange('month'),
      };

      // All 5 fetches run in PARALLEL
      const [today, yesterday, thisWeek, lastWeek, thisMonth] = await Promise.all([
        fetchPeriod(ranges.today.start,     ranges.today.end),
        fetchPeriod(ranges.yesterday.start, ranges.yesterday.end),
        fetchPeriod(ranges.thisWeek.start,  ranges.thisWeek.end),
        fetchPeriod(ranges.lastWeek.start,  ranges.lastWeek.end),
        fetchPeriod(ranges.thisMonth.start, ranges.thisMonth.end),
      ]);

      setPeriods({ today, yesterday, thisWeek, lastWeek, thisMonth });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ─ Derived computations (all memoised) ─
  const health = useMemo(() =>
    computeHealthScore(periods.today, periods.thisWeek, periods.lastWeek),
    [periods]
  );

  const recommendations = useMemo(() =>
    buildRecommendations(periods.today, periods.thisWeek, periods.lastWeek, periods.thisMonth),
    [periods]
  );

  const gradeInfo = health.overall !== null ? gradeLabel(health.overall) : null;

  // Revenue values
  const todayRev     = getRevCents(periods.today)     / 100;
  const yesterdayRev = getRevCents(periods.yesterday) / 100;
  const weekRev      = getRevCents(periods.thisWeek)  / 100;
  const lastWeekRev  = getRevCents(periods.lastWeek)  / 100;
  const monthRev     = getRevCents(periods.thisMonth) / 100;

  const todayVsYest  = deltaStr(todayRev, yesterdayRev);
  const weekVsLast   = deltaStr(weekRev, lastWeekRev);

  // Tickets
  const todayTix  = getTickets(periods.today);
  const weekTix   = getTickets(periods.thisWeek);

  // Benchmarks
  const avgTkt    = weekTix > 0 ? weekRev / weekTix : 0;
  const discRate  = weekRev > 0 ? (getDiscCents(periods.thisWeek) / 100 / weekRev) * 100 : 0;
  const returnTix = getTickets(periods.thisWeek) > 0
    ? (getReturnQty(periods.thisWeek) / getTickets(periods.thisWeek)) * 100 : 0;
  const cateringMix = weekRev > 0
    ? (getCateringRevCents(periods.thisWeek) / 100 / weekRev) * 100 : 0;

  // Category growth table
  const catGrowth = useMemo(() => {
    const curr  = getCategoryRevMap(periods.thisWeek);
    const prior = getCategoryRevMap(periods.lastWeek);
    const all   = [...new Set([...Object.keys(curr), ...Object.keys(prior)])];
    return all
      .map(n => ({ name: n, curr: (curr[n] || 0)/100, prior: (prior[n] || 0)/100, d: deltaStr(curr[n]||0, prior[n]||0) }))
      .filter(x => x.curr > 0 || x.prior > 0)
      .sort((a, b) => b.curr - a.curr);
  }, [periods]);

  // Daily sparkline — approximate using available data
  const sparkValues = [
    getRevCents(periods.lastWeek) / 100 / 7,
    getRevCents(periods.lastWeek) / 100 / 7,
    getRevCents(periods.thisWeek) / 100 / 7,
    getRevCents(periods.thisWeek) / 100 / 7,
    getRevCents(periods.today)    / 100,
  ].filter(v => v > 0);

  return (
    <div className="gt__wrap">
      {/* Header */}
      <div className="gt__header">
        <div>
          <div className="gt__title">📊 Business Growth Intelligence</div>
          <div className="gt__subtitle">5 periods fetched in parallel · Updated live · Industry benchmarks from NRN & Restaurant365 2026</div>
        </div>
        <button className="gt__refresh" onClick={fetchAll} disabled={loading}>
          {loading ? '⏳' : '🔄'} Refresh
        </button>
      </div>

      {loading && (
        <div className="gt__loading">
          <span className="sq__spinner" />
          Fetching 5 periods in parallel (today, yesterday, this week, last week, this month)…
        </div>
      )}
      {error && !loading && <div className="gt__error">⚠️ {error}</div>}

      {!loading && !error && periods.thisWeek && (
        <>
          {/* ── Row 1: Health scorecard + Revenue Velocity ── */}
          <div className="gt__row-2">
            {/* Health Scorecard */}
            <div className="gt__scorecard" style={gradeInfo ? { borderColor: gradeInfo.color } : {}}>
              <div className="gt__scorecard-title">🏥 Business Health Score</div>
              {gradeInfo && (
                <div className="gt__grade-wrap">
                  <div className="gt__grade" style={{ background: gradeInfo.bg, color: gradeInfo.color }}>
                    {gradeInfo.grade}
                  </div>
                  <div className="gt__grade-score">{health.overall}/100</div>
                </div>
              )}
              <div className="gt__score-items">
                {health.scores.map(s => (
                  <div key={s.label} className="gt__score-item">
                    <div className="gt__score-lbl">{s.label}</div>
                    <div className="gt__score-bar-wrap">
                      <div
                        className="gt__score-bar"
                        style={{ width: `${s.score}%`, background: s.score >= 75 ? '#16a34a' : s.score >= 50 ? '#d97706' : '#e11d48' }}
                      />
                    </div>
                    <div className="gt__score-detail">{s.detail}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Velocity */}
            <VelocityCard
              weekRev={weekRev}
              lastWeekRev={lastWeekRev}
              weekLabel="This Week"
              lastWeekLabel="Last Week"
              dailyRevs={sparkValues}
            />
          </div>

          {/* ── Row 2: KPI Snapshot ── */}
          <div className="gt__kpi-row">
            {[
              { icon: '☀️', label: 'Today',     val: fmt$(todayRev),  sub: `${fmtN(todayTix)} tickets · ${fmtPct(todayVsYest)} vs yesterday` },
              { icon: '📅', label: 'This Week',  val: fmt$(weekRev),   sub: `${fmtN(weekTix)} tickets · ${fmtPct(weekVsLast)} vs last week` },
              { icon: '🗓', label: 'This Month', val: fmt$(monthRev),  sub: `${fmtN(getTickets(periods.thisMonth))} tickets` },
              { icon: '📊', label: 'Avg Ticket', val: fmt$(avgTkt),    sub: `Benchmark: $${BENCHMARKS.avgTicket.low}–$${BENCHMARKS.avgTicket.high}` },
            ].map(k => (
              <div key={k.label} className="gt__kpi">
                <div className="gt__kpi-icon">{k.icon}</div>
                <div className="gt__kpi-lbl">{k.label}</div>
                <div className="gt__kpi-val">{k.val}</div>
                <div className="gt__kpi-sub">{k.sub}</div>
              </div>
            ))}
          </div>

          {/* ── Row 3: Industry benchmarks ── */}
          <div className="gt__section">
            <div className="gt__section-title">📏 Industry Benchmark Comparison <span className="gt__source">(Restaurant365 + NRN 2026)</span></div>
            <div className="gt__benchmarks">
              {avgTkt > 0 && (
                <BenchmarkMeter label="Avg Ticket / Order" value={avgTkt}
                  low={BENCHMARKS.avgTicket.low} mid={BENCHMARKS.avgTicket.mid} high={BENCHMARKS.avgTicket.high}
                  unit="$" />
              )}
              <BenchmarkMeter label="Discount Rate" value={discRate}
                low={BENCHMARKS.discountRate.low} mid={BENCHMARKS.discountRate.mid} high={BENCHMARKS.discountRate.high}
                unit="%" lowerIsBetter />
              <BenchmarkMeter label="Return / Refund Rate" value={returnTix}
                low={BENCHMARKS.returnRate.low} mid={BENCHMARKS.returnRate.mid} high={BENCHMARKS.returnRate.high}
                unit="%" lowerIsBetter />
              {cateringMix > 0 && (
                <BenchmarkMeter label="Catering Revenue Mix" value={cateringMix}
                  low={BENCHMARKS.cateringMix.low} mid={BENCHMARKS.cateringMix.mid} high={BENCHMARKS.cateringMix.high}
                  unit="%" />
              )}
            </div>
          </div>

          {/* ── Row 4: Category growth table (this week vs last week) ── */}
          {catGrowth.length > 0 && (
            <div className="gt__section">
              <div className="gt__section-title">📁 Category Growth (This Week vs Last Week)</div>
              <div className="gt__cat-table">
                <div className="gt__cat-head">
                  <span>Category</span>
                  <span>This Week</span>
                  <span>Last Week</span>
                  <span>Change</span>
                </div>
                {catGrowth.map(c => (
                  <div key={c.name} className="gt__cat-row">
                    <span className="gt__cat-name">{c.name}</span>
                    <span className="gt__cat-curr">{fmt$(c.curr)}</span>
                    <span className="gt__cat-prior">{fmt$(c.prior)}</span>
                    <span className={`gt__cat-delta ${c.d === null ? '' : c.d >= 0 ? 'gt__delta--up' : 'gt__delta--down'}`}>
                      {fmtPct(c.d)} {c.d !== null ? (c.d >= 0 ? '▲' : '▼') : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Row 5: Catering spotlight ── */}
          <div className="gt__catering-banner">
            <div className="gt__catering-icon">🍱</div>
            <div className="gt__catering-body">
              <div className="gt__catering-title">Catering = #1 Growth Engine (NRN, May 2026)</div>
              <div className="gt__catering-text">
                91% of U.S. workplaces plan to spend the same or <strong>more</strong> on catering in 2026.
                B2B catering AUVs are <strong>10× mealtime revenue</strong> for top food chains.
                Weekly recurring workplace orders (Friday lunch, Monday breakfast) are the most predictable revenue stream available to food businesses today.
                {getCateringRevCents(periods.thisWeek) > 0
                  ? ` You earned ${fmt$(getCateringRevCents(periods.thisWeek)/100)} in catering this week (${cateringMix.toFixed(1)}% of revenue). Goal: reach 20–35%.`
                  : ' You currently have no tracked catering revenue — this is your biggest untapped growth opportunity.'}
              </div>
            </div>
          </div>

          {/* ── Row 6: Growth recommendations ── */}
          <div className="gt__section">
            <div className="gt__section-title">🎯 Growth Recommendations</div>
            <div className="gt__recs">
              {recommendations.map((rec, i) => (
                <div key={i} className={`gt__rec gt__rec--${rec.priority}`}>
                  <div className="gt__rec-head">
                    <span className="gt__rec-icon">{rec.icon}</span>
                    <span className="gt__rec-title">{rec.title}</span>
                    <span className={`gt__rec-badge gt__rec-badge--${rec.priority}`}>
                      {rec.priority === 'high' ? '🔴 High' : rec.priority === 'medium' ? '🟡 Medium' : '🟢 Low'}
                    </span>
                  </div>
                  <div className="gt__rec-body">{rec.body}</div>
                  <div className="gt__rec-action">▶ {rec.action}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Row 7: Monthly run-rate projection ── */}
          {todayRev > 0 && (
            <div className="gt__projection">
              <div className="gt__proj-title">📅 Monthly Run-Rate Projection</div>
              <div className="gt__proj-grid">
                {[
                  { label: 'At current daily pace',  val: todayRev * 30 },
                  { label: '+10% growth target',      val: todayRev * 30 * 1.10 },
                  { label: '+20% with catering push', val: todayRev * 30 * 1.20 },
                  { label: '+35% full catering mix',  val: todayRev * 30 * 1.35 },
                ].map(p => (
                  <div key={p.label} className="gt__proj-item">
                    <div className="gt__proj-label">{p.label}</div>
                    <div className="gt__proj-val">{fmt$(p.val)}</div>
                  </div>
                ))}
              </div>
              <div className="gt__proj-note">* Projections based on today's revenue × 30 days. Actual results vary with seasonality and catering mix.</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
